import { apiService } from "../api.service";

// Requests
import {
  RegisterRequest,
  UpdateProfileRequest
} from "../../models/api/requests";

import {
  Auth,
  User,
  MenuItem,
  Branch,
  DashboardData
} from "../../models/app/domain";
import { isEmpty } from "../../utils";

/**
 * Login user Request
 * @param loginFormValues - LoginFormModel
 * @return - AuthenticationResponse || ErrorResponse
 */
export const loginUserRequest = async loginFormValues => {
  const payload = {
    email: loginFormValues.email,
    password: loginFormValues.password
  };
  const response = await apiService.post(
    "/auth/login?with_additional_data=true&with_dashboard_data=true",
    payload
  );
  if (response.hasError) {
    return response;
  }
  return {
    auth: new Auth(response.data.auth),
    user: new User(response.data.user),
    menuItems: !isEmpty(response.data.menu_items)
      ? response.data.menu_items.map(item => new MenuItem(item))
      : [],
    branches: !isEmpty(response.data.branches)
      ? response.data.branches.map(branch => new Branch(branch))
      : [],
    dashboardData: new DashboardData(response.data.dashboard_data)
  };
};

/**
 * Register user Request
 * @param registerFormValues - {account, business}
 * @return - ErrorResponse || AuthenticationResponse
 */
export const registerUserRequest = async registerFormValues => {
  const payload = new RegisterRequest(registerFormValues);
  payload.business.removeEmptyValues();
  const response = await apiService.post(
    "/auth/registration?with_additional_data=true&with_dashboard_data=true",
    payload
  );
  if (response.hasError) {
    return response;
  }
  return {
    auth: new Auth(response.data.auth),
    user: new User(response.data.user),
    menuItems: !isEmpty(response.data.menu_items)
      ? response.data.menu_items.map(item => new MenuItem(item))
      : [],
    branches: !isEmpty(response.data.branches)
      ? response.data.branches.map(branch => new Branch(branch))
      : [],
    dashboardData: new DashboardData(response.data.dashboard_data)
  };
};

/**
 * Fetch user data with credentials in local storage
 * @return - AuthenticationResponse || ErrorResponse
 */
export const fetchAuthenticatedUserRequest = async () => {
  const response = await apiService.get(
    "/users?with_additional_data=true&with_dashboard_data=true"
  );
  if (response.hasError) {
    return response;
  }
  return {
    auth: new Auth(response.data.auth),
    user: new User(response.data.user),
    menuItems: !isEmpty(response.data.menu_items)
      ? response.data.menu_items.map(item => new MenuItem(item))
      : [],
    branches: !isEmpty(response.data.branches)
      ? response.data.branches.map(branch => new Branch(branch))
      : [],
    dashboardData: new DashboardData(response.data.dashboard_data)
  };
};

/**
 * Update user profile request
 * @param profileFormValues - ProfileForm updated values
 * @param initialProfileFormValues - ProfileForm initial data
 * @return - UserResponse || ErrorResponse
 */
export const updateProfileRequest = async (
  profileFormValues,
  initialProfileFormValues
) => {
  const payload = new UpdateProfileRequest(profileFormValues);
  payload.removeSameValues(initialProfileFormValues);
  const response = await apiService.patch("/users", payload);
  if (response.hasError) {
    return response;
  }
  return { user: new User(response.data.user) };
};

/**
 * Forgot password request
 * @param forgotPasswrodValues - ForgotPasswrodFormModel
 * @return - MessageResponse || ErrorResponse
 */
export const forgotPasswordRequest = async forgotPasswrodValues => {
  const payload = { email: forgotPasswrodValues.email };
  const response = await apiService.post("/auth/password/forgot", payload);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Change user password request
 * @param changePasswordValues - ChangePasswordRequest
 * @return - MessageResponse || ErrorResponse
 */
export const changePasswordRequest = async changePasswordValues => {
  const payload = {
    old_password: changePasswordValues.oldPassword,
    password: changePasswordValues.password,
    password_confirmation: changePasswordValues.passwordConfirmation
  };
  const response = await apiService.post("/auth/password/change", payload);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

export const fetchChatToken = async () => {
  const response = await apiService.get("/chat/auth");
  if (response.hasError) {
    return response;
  }
  return response.data;
};
